// Substrings that identify frames from our own bundle: source-mapped webpack paths
// (projectRoot is webpack:///./app/javascript) and raw Shakapacker pack URLs (/packs/js/...).
const OWN_CODE_PATTERNS = ["webpack://", "app/javascript", "/packs/"]

const isOwnCodeFrame = (file) => OWN_CODE_PATTERNS.some((pattern) => String(file).includes(pattern))

/**
 * True when an error originates entirely outside our webpack bundle — e.g. tracking
 * pixels or widgets injected by practices on their custom-domain sites (PxGrabber /
 * web2mailbox, GTM custom tags), or browser extensions. Uncaught errors from those
 * scripts land in Honeybadger but are not actionable in application code.
 *
 * Conservative: only drops when there is stack information and none of it points at
 * our code. Errors without any backtrace/stack are kept (manual message-only notifies).
 *
 * @param {{ stack?: string, backtrace?: Array<{ file?: string, method?: string, number?: number, column?: number } | null | undefined> }} [error]
 * @returns {boolean}
 */
export function isForeignScriptError({ stack = "", backtrace = [] } = {}) {
  const frames = (Array.isArray(backtrace) ? backtrace : []).filter((frame) => frame && frame.file)
  if (frames.length > 0) return !frames.some((frame) => isOwnCodeFrame(frame.file))
  if (stack) return !isOwnCodeFrame(stack)
  return false
}

// DOM / React-fiber markers in circular-JSON TypeErrors (Honeybadger breadcrumbs or
// extensions serializing an event target / node). Fault 133253685 = HTMLAnchorElement.
const CIRCULAR_DOM_JSON_MARKERS = [
  "PointerEvent",
  "HTMLAnchorElement",
  "HTMLButtonElement",
  "HTMLDivElement",
  "HTMLElement",
  "SVGElement",
  "__reactFiber"
]

/**
 * True when JSON.stringify hit a DOM node / React fiber cycle — non-actionable noise
 * (HB client serializing breadcrumbs/context, or third-party code). Not a real app bug.
 *
 * @param {{ message?: string, stack?: string }} [error]
 * @returns {boolean}
 */
export function isCircularDomJsonNoise({ message = "", stack = "" } = {}) {
  const text = [message, stack].filter(Boolean).join(" ")
  if (!text.includes("Converting circular structure to JSON")) return false
  return CIRCULAR_DOM_JSON_MARKERS.some((marker) => text.includes(marker))
}

/**
 * Minified third-party "t is not a function" (VWO / trackers / crawlers).
 * Fault 133309416: Facebook meta-webindexer on custom domain, top frame
 * `<anonymous>` `i.onerror` — often mixed with our `/packs/` global handler frames,
 * so a strict "no app/javascript in stack" check is not enough.
 *
 * @param {{ message?: string, stack?: string, backtrace?: Array<{ file?: string, method?: string, number?: number, column?: number } | null | undefined> }} [error]
 * @returns {boolean}
 */
export function isMinifiedAnonymousNotAFunctionNoise({ message = "", stack = "", backtrace = [] } = {}) {
  if (!String(message).includes("t is not a function")) return false

  if (isForeignScriptError({ stack, backtrace })) return true

  const frames = (Array.isArray(backtrace) ? backtrace : []).filter((frame) => frame && frame.file)
  if (frames.length === 0) {
    return !OWN_CODE_PATTERNS.some((pattern) => String(stack).includes(pattern))
  }

  const top = frames[0]
  const topFile = String(top.file)
  const topMethod = String(top.method || "")
  if (topFile.includes("<anonymous>") && /onerror/i.test(topMethod)) return true

  return false
}

/**
 * @param {{ message?: string, name?: string, stack?: string, backtrace?: Array<{ file?: string, method?: string, number?: number, column?: number } | null | undefined> }} [error]
 * @returns {boolean}
 */
export function shouldIgnoreHoneybadgerNoise({ message = "", name = "", stack = "", backtrace = [] } = {}) {
  const combinedText = [name, message, stack].filter(Boolean).join(" ").toLowerCase()

  if (isCircularDomJsonNoise({ message, stack })) return true
  if (isMinifiedAnonymousNotAFunctionNoise({ message, stack, backtrace })) return true

  return (
    combinedText.includes("lastsettimerid") ||
    combinedText.includes('can\'t access property "lastsettimerid"') ||
    combinedText.includes("cannot set properties of undefined (setting 'lastsettimerid')") ||
    combinedText.includes("undefined is not an object (evaluating 'this.lastsettimerid")
  )
}

export function safeHoneybadgerNotify(HoneybadgerClient, errorOrNotice, options) {
  const message = errorOrNotice?.message || String(errorOrNotice || "")
  const name = errorOrNotice?.name || ""
  const stack = errorOrNotice?.stack || ""
  const backtrace = errorOrNotice?.backtrace || []

  if (shouldIgnoreHoneybadgerNoise({ message, name, stack, backtrace })) return
  HoneybadgerClient.notify(errorOrNotice, options)
}
